import React from 'react'
import styled, { css } from 'styled-components'
import {
  ariaAttr,
  dataAttr,
  isNumber,
  mergeClassnames,
  useCallbackRef,
  useMergeRefs
} from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import { CloseButton } from '../closeButton/closeButton'

export const Input = React.forwardRef(
  (
    {
      name,
      disabled = false,
      placeholder,
      label,
      htmlType = 'text',
      value = '',
      width: _width,
      maxWidth,
      fullWidth,
      htmlAutocomplete = 'off',
      readOnly = false,
      invalid = false,
      required = false,
      isClearable = false,
      labelLeft,
      labelRight,
      contentLeft,
      contentRight,
      removeContentLeftStyling = false,
      isContentLeftClickable = false,
      removeContentRightStyling = false,
      isContentRightClickable = false,
      onContentLeftClick: _onContentLeftClick,
      onContentRightClick: _onContentRightClick,
      onClearClick: _onClearClick,
      onChange: _onChange,
      onFocus: _onFocus,
      onBlur: _onBlur,
      className,
      ...rest
    },
    ref
  ) => {
    const [selfValue, setSelfValue] = React.useState(value || '')
    const [isFocused, setIsFocused] = React.useState(false)
    const inputRef = React.useRef(null)
    const refs = useMergeRefs(ref, inputRef)

    const onFocus = useCallbackRef(_onFocus)
    const onBlur = useCallbackRef(_onBlur)
    const onChange = useCallbackRef(_onChange)
    const onContentLeftClick = useCallbackRef(_onContentLeftClick)
    const onContentRightClick = useCallbackRef(_onContentRightClick)
    const onClearClick = useCallbackRef(_onClearClick)

    const _className = mergeClassnames('mote-input-root', className)

    const width = React.useMemo(() => {
      if (fullWidth) return '100%'
      if (_width) return isNumber(_width) ? `${_width}px` : _width
      return 'initial'
    }, [fullWidth, _width])

    const handleChange = React.useCallback(
      event => {
        if (disabled || readOnly) return

        setSelfValue(event.target.value)
        onChange && onChange(event)
      },
      [disabled, readOnly, onChange]
    )

    const handleFocus = React.useCallback(
      event => {
        setIsFocused(true)
        onFocus?.(event)
      },
      [onFocus]
    )

    const handleBlur = React.useCallback(
      event => {
        setIsFocused(false)
        onBlur?.(event)
      },
      [onBlur]
    )

    const handleClear = React.useCallback(
      event => {
        if (disabled || readOnly) return

        event.preventDefault()
        event.stopPropagation()
        event.nativeEvent.stopImmediatePropagation()

        setSelfValue('')
        onClearClick?.(event)

        if (!inputRef.current) return

        const element = inputRef.current

        const changeEvent = {
          ...event,
          target: element,
          currentTarget: element
        }

        changeEvent.target.value = ''
        onChange?.(changeEvent)
        inputRef.current.focus()
      },
      [disabled, onChange, onClearClick, readOnly]
    )

    const handleContentClick = React.useCallback(
      (key, event) => {
        if (disabled) return

        const action = key === 'left' ? onContentLeftClick : onContentRightClick

        action?.(event)
      },
      [disabled, onContentLeftClick, onContentRightClick]
    )

    React.useEffect(() => {
      setSelfValue(value || '')
    }, [value])

    return (
      <Root
        className={_className}
        width={width}
        maxWidth={maxWidth}
        data-disabled={dataAttr(disabled)}
      >
        {label && <LabelWrapper>{label}</LabelWrapper>}
        <InputWrapper className='mote-input-wrapper'>
          <InputGroup
            as={label ? 'div' : 'label'}
            className='mote-input-group'
            data-focused={dataAttr(isFocused)}
            data-disabled={dataAttr(disabled)}
            data-invalid={dataAttr(invalid)}
          >
            {labelLeft && (
              <InputLabel className='mote-input__label-left' placement='left'>
                {labelLeft}
              </InputLabel>
            )}
            {contentLeft && (
              <InputContent
                className='mote-input__content-left'
                removeStyles={removeContentLeftStyling}
                isClickable={isContentLeftClickable}
                onClick={event => handleContentClick('left', event)}
              >
                {contentLeft}
              </InputContent>
            )}
            <StyledInput
              ref={refs}
              className='mote-input'
              name={name}
              value={selfValue}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder}
              type={htmlType}
              autoCorrect='off'
              autoCapitalize='off'
              spellCheck='false'
              autoComplete={htmlAutocomplete}
              readOnly={readOnly}
              invalid={invalid}
              hasLeftContent={!!contentLeft}
              hasRightContent={!!contentRight}
              aria-invalid={ariaAttr(invalid)}
              aria-required={ariaAttr(required)}
              aria-readonly={ariaAttr(readOnly)}
              aria-placeholder={ariaAttr(placeholder)}
              disabled={disabled}
              {...rest}
            />
            {isClearable && (
              <InputClearButton
                className='mote-input__clear-button'
                visible={Boolean(selfValue)}
                hasContentRight={!!contentRight}
                disabled={disabled || readOnly}
                onClick={handleClear}
              />
            )}
            {contentRight && (
              <InputContent
                className='mote-input__content-right'
                removeStyles={removeContentRightStyling}
                isClickable={isContentRightClickable}
                onClick={event => handleContentClick('right', event)}
              >
                {contentRight}
              </InputContent>
            )}
            {labelRight && (
              <InputLabel className='mote-input__label-right' placement='right'>
                {labelRight}
              </InputLabel>
            )}
          </InputGroup>
        </InputWrapper>
      </Root>
    )
  }
)

if (__DEV__) {
  Input.displayName = 'Input'
}

const Root = styled.div`
  width: ${({ width }) => width};
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  box-sizing: border-box;

  max-width: ${({ maxWidth }) =>
    isNumber(maxWidth) ? `${maxWidth}px` : maxWidth};

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledInput = styled.input`
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: 0 6px;
  margin: 4px 10px;
  font-weight: normal;
  font-size: 15px;
  line-height: 120%;
  color: #252523;
  appearance: none;
  border: none;
  border-radius: 0;
  outline: none;
  background: transparent;
  transition: all 150ms ease-out;

  &::placeholder {
    color: #b2b2cc;
    font-weight: normal;
    text-transform: none;
    transition: opacity 150ms ease;
  }

  &:focus {
    &::placeholder {
      opacity: 0;
    }
  }

  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
  }

  ${({ hasLeftContent, hasRightContent }) => {
    if (hasLeftContent)
      return css`
        margin-left: 0;
        padding-left: 0;
      `

    if (hasRightContent)
      return css`
        margin-right: 0;
        padding-right: 0;
      `
  }};
`

const LabelWrapper = styled.label`
  font-weight: 500;
  font-size: 15px;
  line-height: 120%;
  color: #232323;
  margin-bottom: 8px;
`

const InputWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  height: 40px;
`

const InputGroup = styled.div`
  height: 100%;
  display: inline-flex;
  flex-direction: row;
  flex: 1;
  align-items: center;
  vertical-align: middle;
  user-select: none;
  border-radius: 4px;
  border: 1px solid #d1d1e0;
  background: #ffffff;
  transition: all 150ms ease;
  box-sizing: border-box;

  &:hover {
    border-color: #b2b2cc;
  }

  &[data-focused] {
    outline: none;
    border: 1px solid #ac0ae8;
    box-shadow: 0 0 0 1px #ac0ae8;

    &::placeholder {
      opacity: 0;
    }
  }

  &[data-disabled] {
    pointer-events: none;
    cursor: not-allowed;
  }

  &[data-invalid]:not([data-disabled]) {
    border-color: #f44336;

    &:hover {
      border-color: #f44336;
    }

    &[data-focused] {
      border-color: #f44336;
      box-shadow: 0 0 0 1px #f44336;
    }
  }
`

const InputLabel = styled.span`
  position: relative;
  display: inline-flex;
  width: initial;
  height: 100%;
  color: #b2b2cc;
  font-weight: normal;
  font-size: 15px;
  line-height: 120%;
  align-items: center;
  pointer-events: none;
  margin: 0;
  padding: 0 12px;

  &:after {
    display: none;
  }

  &:before: {
    display: none;
  }

  ${({ placement }) => {
    switch (placement) {
      case 'left':
        return css`
          border-radius: 4px 0 0 4px;
          padding-right: 0;
        `
      case 'right':
        return css`
          border-radius: 0 4px 4px 0;
          padding-left: 0;
        `
    }
  }}
`

const InputContent = styled.span`
  ${({ removeStyles }) =>
    !removeStyles &&
    css`
      display: flex;
      box-sizing: content-box;
      width: auto;
      height: 100%;
      align-items: center;
      vertical-align: middle;
      margin: 0;
      padding: 0 6px;
      color: #232323;
      line-height: 120%;
      position: relative;
      cursor: default;
      pointer-events: none;
    `}

  ${({ isClickable }) =>
    isClickable &&
    css`
      pointer-events: auto;
      cursor: pointer;
    `}
`

const InputClearButton = styled(CloseButton).attrs({
  size: 24,
  'aria-label': 'Clear'
})`
  position: absolute;
  right: 6px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  border-radius: 100%;
  color: #646f7b;
  opacity: 0;
  visibility: hidden;
  transform: translateX(20%);
  transition-duration: 150ms;
  transition-timing-function: ease;
  transition-property: color, background-color, opacity, transform;

  ${({ visible }) =>
    visible &&
    css`
      visibility: visible;
      transform: translateX(0);
      opacity: 1;
    `}

  ${({ hasContentRight }) =>
    hasContentRight &&
    css`
      right: unset;
      position: relative;
      transform: translateX(30%);
    `}
`
